import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Flame, Check, Award, X, Lock, Sparkles } from 'lucide-react';
import { listPrograms, myPrograms, enrollProgram, checkinProgram } from '../api';
import { useToast } from '../hooks/use-toast';

const isAuthed = () => !!localStorage.getItem('pe_cust_token');

const Programs = () => {
  const [programs, setPrograms] = useState([]);
  const [mine, setMine] = useState([]);
  const [active, setActive] = useState(null);
  const { toast } = useToast();

  const loadMine = async () => {
    if (!isAuthed()) return;
    try { setMine(await myPrograms()); } catch { /* ignore */ }
  };

  useEffect(() => {
    listPrograms().then(setPrograms).catch(() => {});
    loadMine();
  }, []);

  const enrollmentFor = (pid) => mine.find((m) => m.program_id === pid);

  const enroll = async (p) => {
    if (!isAuthed()) { toast({ title: 'Sign in to join', description: 'Create a free account to start this challenge.' }); return; }
    try { await enrollProgram(p.id); await loadMine(); toast({ title: 'You\u2019re in! \u2728', description: `Welcome to ${p.title}.` }); }
    catch { toast({ title: 'Could not enroll' }); }
  };

  const checkin = async (p, day) => {
    try {
      const e = await checkinProgram(p.id, day);
      setMine((prev) => prev.map((m) => (m.program_id === p.id ? e : m)));
      if (e.completed && e.badge_earned) toast({ title: `Badge earned: ${e.badge_earned} \ud83c\udfc6`, description: `You completed ${p.title}!` });
      else toast({ title: `Day ${day} complete \u2713`, description: `Streak: ${e.streak} day${e.streak === 1 ? '' : 's'}` });
    } catch { toast({ title: 'Check-in failed' }); }
  };

  return (
    <div className="pt-28 pb-24 min-h-screen">
      <div className="max-w-6xl mx-auto px-5">
        <div className="text-center mb-12">
          <p className="font-jost text-xs tracking-[0.4em] text-[#ff4da6] uppercase mb-3">Move · Nourish · Grow</p>
          <h1 className="font-cinzel text-4xl md:text-6xl text-gold-gradient">Programs & Challenges</h1>
          <div className="gold-divider mt-4" />
          <p className="font-cormorant text-xl text-[#f5ecd9]/70 max-w-2xl mx-auto mt-5">Guided challenges to build lasting habits — check in daily, keep your streak, and earn a badge when you finish.</p>
        </div>

        {isAuthed() && mine.length > 0 && (
          <div className="mb-14">
            <h2 className="font-cinzel text-2xl text-[#f0d582] mb-5">My Challenges</h2>
            <div className="grid md:grid-cols-2 gap-5">
              {mine.map((m) => {
                const pct = Math.round((m.checkins.length / m.days) * 100);
                return (
                  <div key={m.id} data-testid={`my-program-${m.program_slug}`} className="card-glass rounded-2xl p-5">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="font-cinzel text-lg text-[#f5ecd9]">{m.program_title}</h3>
                      {m.completed ? <span className="flex items-center gap-1 text-xs text-[#f0d582]"><Award className="h-4 w-4" /> {m.badge_earned}</span>
                        : <span className="flex items-center gap-1 text-xs text-[#ff4da6]"><Flame className="h-4 w-4" /> {m.streak} day streak</span>}
                    </div>
                    <div className="mt-3 h-2 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-gradient-to-r from-[#d4af37] to-[#ff4da6] transition-all duration-500" style={{ width: `${pct}%` }} /></div>
                    <p className="font-jost text-xs text-[#f5ecd9]/50 mt-2">{m.checkins.length} / {m.days} days · {pct}%</p>
                    <button data-testid={`open-program-${m.program_slug}`} onClick={() => setActive(programs.find((p) => p.id === m.program_id))} className="mt-4 text-[11px] uppercase tracking-widest px-4 py-2 rounded-full border border-[#d4af37]/40 text-[#f0d582] hover:bg-[#d4af37]/10">Open & Check In</button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-6">
          {programs.map((p) => {
            const enr = enrollmentFor(p.id);
            return (
              <div key={p.id} data-testid={`program-card-${p.slug}`} className="card-glass rounded-2xl p-6 flex flex-col" style={{ borderTop: `3px solid ${p.color}` }}>
                <p className="font-jost text-[10px] uppercase tracking-widest text-[#f5ecd9]/40">{p.category} · {p.days} days</p>
                <h3 className="font-cinzel text-xl text-[#f5ecd9] mt-2">{p.title}</h3>
                <p className="font-cormorant text-lg text-[#f5ecd9]/65 mt-2 flex-1">{p.description}</p>
                <div className="flex items-center gap-2 mt-4">
                  <button data-testid={`view-program-${p.slug}`} onClick={() => setActive(p)} className="text-[11px] uppercase tracking-widest px-4 py-2 rounded-full border border-[#d4af37]/40 text-[#f0d582] hover:bg-[#d4af37]/10">View Days</button>
                  {enr ? <span className="text-[11px] uppercase tracking-widest text-[#7bb661] flex items-center gap-1"><Check className="h-3.5 w-3.5" /> Enrolled</span>
                    : <button data-testid={`enroll-${p.slug}`} onClick={() => enroll(p)} className="text-[11px] uppercase tracking-widest px-4 py-2 rounded-full text-[#0a0708] font-semibold bg-gradient-to-r from-[#d4af37] to-[#f0d582]">Join Challenge</button>}
                </div>
              </div>
            );
          })}
        </div>

        {!isAuthed() && (
          <p className="text-center font-jost text-sm text-[#f5ecd9]/50 mt-10 flex items-center justify-center gap-2"><Lock className="h-4 w-4" /> <Link to="/account" className="text-[#f0d582] underline">Sign in or create an account</Link> to join challenges and track your progress.</p>
        )}
      </div>

      {active && (
        <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={() => setActive(null)} data-testid="program-detail-modal">
          <div className="card-glass rounded-2xl p-6 max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()} style={{ borderTop: `3px solid ${active.color}` }}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-jost text-[10px] uppercase tracking-widest text-[#f5ecd9]/40">{active.category} · {active.days} days · Badge: {active.badge}</p>
                <h3 className="font-cinzel text-2xl text-[#f0d582] mt-1">{active.title}</h3>
              </div>
              <button onClick={() => setActive(null)} className="text-[#f5ecd9]/60 hover:text-[#f0d582] text-2xl leading-none">×</button>
            </div>
            {(() => {
              const enr = enrollmentFor(active.id);
              const done = new Set(enr?.checkins || []);
              const nextDay = enr ? (enr.checkins.length + 1) : null;
              return (
                <>
                  {!enr && <button data-testid="modal-enroll" onClick={() => enroll(active)} className="mt-4 w-full rounded-full py-2.5 text-xs uppercase tracking-widest font-semibold text-[#0a0708] bg-gradient-to-r from-[#d4af37] to-[#f0d582]">Join This Challenge</button>}
                  <div className="mt-5 space-y-2 max-h-[55vh] overflow-y-auto pr-1">
                    {active.tasks.map((t) => {
                      const isDone = done.has(t.day);
                      const isNext = t.day === nextDay;
                      return (
                        <div key={t.day} data-testid={`day-row-${t.day}`} className={`flex items-start gap-3 rounded-xl p-3 border ${isDone ? 'border-[#7bb661]/30 bg-[#7bb661]/5' : isNext ? 'border-[#ff4da6]/30 bg-[#ff4da6]/5' : 'border-white/8'}`}>
                          <div className={`h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-jost ${isDone ? 'bg-[#7bb661] text-white' : 'border border-[#d4af37]/40 text-[#f0d582]'}`}>{isDone ? <Check className="h-4 w-4" /> : t.day}</div>
                          <div className="flex-1 min-w-0">
                            <p className="font-jost text-sm text-[#f5ecd9]">{t.title}</p>
                            <p className="font-jost text-xs text-[#f5ecd9]/50 mt-0.5">{t.detail}</p>
                          </div>
                          {enr && !isDone && (
                            <button data-testid={`checkin-day-${t.day}`} onClick={() => checkin(active, t.day)} className="text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border border-[#d4af37]/40 text-[#f0d582] hover:bg-[#d4af37]/10 flex-shrink-0">Done</button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default Programs;
